import { useState } from 'react';
import Icon from '../AppIcon';

interface ContactFormToggleProps {
  className?: string;
}

type ContactMethod = 'form' | 'email';

const ContactFormToggle = ({ className = '' }: ContactFormToggleProps) => {
  const [activeMethod, setActiveMethod] = useState<ContactMethod>('form');

  return (
    <div className={`space-y-6 ${className}`}>
      <div className="flex items-center justify-center space-x-4">
        <button
          onClick={() => setActiveMethod('form')}
          className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold transition-all duration-250 ${
            activeMethod === 'form'
              ? 'bg-primary text-primary-foreground shadow-glass' :'bg-muted/30 text-muted-foreground hover:bg-muted/50'
          }`}
        >
          <Icon name="FileText" size={20} />
          <span>Contact Form</span>
        </button>
        <button
          onClick={() => setActiveMethod('email')}
          className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold transition-all duration-250 ${
            activeMethod === 'email' ?'bg-primary text-primary-foreground shadow-glass' :'bg-muted/30 text-muted-foreground hover:bg-muted/50'
          }`}
        >
          <Icon name="Mail" size={20} />
          <span>Direct Email</span>
        </button>
      </div>

      <div className="glass-effect rounded-lg p-8 animate-fade-in">
        {activeMethod === 'form' ? (
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <h3 className="text-foreground font-semibold text-xl">
                Start Your Project Discussion
              </h3>
              <p className="text-muted-foreground">
                Fill out our detailed project form to help us understand your requirements
              </p>
            </div>

            <div className="bg-muted/20 rounded-lg p-6 space-y-4">
              <div className="flex items-start space-x-3">
                <Icon name="CheckCircle2" size={20} className="text-success mt-1 flex-shrink-0" />
                <div>
                  <h4 className="text-foreground font-medium">Quick Response</h4>
                  <p className="text-muted-foreground text-sm">
                    Our team reviews submissions within 48 Business working hours
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Icon name="Shield" size={20} className="text-success mt-1 flex-shrink-0" />
                <div>
                  <h4 className="text-foreground font-medium">NDA Protection</h4>
                  <p className="text-muted-foreground text-sm">
                    All project details are kept strictly confidential
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Icon name="Users" size={20} className="text-success mt-1 flex-shrink-0" />
                <div>
                  <h4 className="text-foreground font-medium">Expert Consultation</h4>
                  <p className="text-muted-foreground text-sm">
                    Direct access to our technical team
                  </p>
                </div>
              </div>
            </div>

            <a
              href="https://docs.google.com/forms/d/e/1FAIpQLSfy0U-ArMKkyMgt_7gKUYvbpzNIkBp704j5vi9HKO8KC9Ik_w/viewform"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center space-x-2 w-full px-8 py-4 rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-all duration-250 shadow-glass"
            >
              <span>Open Contact Form</span>
              <Icon name="ExternalLink" size={20} />
            </a>

            <p className="text-center text-muted-foreground text-sm">
              Form opens in a new window for your convenience
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <h3 className="text-foreground font-semibold text-xl">
                Direct Email Contact
              </h3>
              <p className="text-muted-foreground">
                Prefer email? Reach out directly to our team
              </p>
            </div>

            <div className="bg-muted/20 rounded-lg p-6 space-y-4">
              <div className="flex items-center justify-center space-x-3">
                <Icon name="Mail" size={24} className="text-primary" />
                <a
                  href="mailto:dhavajaresearch@gmail.com"
                  className="text-foreground font-mono text-lg hover:text-primary transition-colors duration-250"
                >
                  dhavajaresearch@gmail.com
                </a>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="text-foreground font-medium text-center">
                Please Include in Your Email:
              </h4>
              <ul className="space-y-2 text-muted-foreground text-sm">
                <li className="flex items-start space-x-2">
                  <Icon name="ChevronRight" size={16} className="text-primary mt-1 flex-shrink-0" />
                  <span>Your name and company details</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="ChevronRight" size={16} className="text-primary mt-1 flex-shrink-0" />
                  <span>Project overview and technical requirements</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="ChevronRight" size={16} className="text-primary mt-1 flex-shrink-0" />
                  <span>Expected timeline and budget range</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="ChevronRight" size={16} className="text-primary mt-1 flex-shrink-0" />
                  <span>Any specific technical challenges or constraints</span>
                </li>
              </ul>
            </div>

            <a
              href="mailto:dhavajaresearch@gmail.com?subject=Project Inquiry&body=Name:%0D%0ACompany:%0D%0AProject Overview:%0D%0ATechnical Requirements:%0D%0ATimeline:%0D%0ABudget Range:"
              className="flex items-center justify-center space-x-2 w-full px-8 py-4 rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-all duration-250 shadow-glass"
            >
              <Icon name="Send" size={20} />
              <span>Compose Email</span>
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactFormToggle;